import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import { Calendar, MapPin, Clock, ArrowLeft } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
export function EventDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Mock event data - in production this would come from an API
  const event = {
    id: slug,
    date: '15 Janvier 2025',
    time: '14h00 - 17h00',
    title: 'Rencontre avec le Wali de Marrakech',
    location: 'Wilaya de Marrakech, Avenue Mohammed V',
    image: 'https://images.unsplash.com/photo-1597212618440-806262de4f6b?w=1200',
    description: 'Rencontre stratégique avec le Wali de la région de Marrakech-Safi pour discuter des enjeux majeurs du secteur de la restauration et du développement touristique de la région.',
    content: `
      Cette rencontre importante réunira les membres du bureau de l'ARWM et le Wali de Marrakech pour aborder plusieurs sujets cruciaux pour le développement du secteur de la restauration dans notre région.

      **Points à l'ordre du jour :**

      1. **Situation actuelle du secteur** - Présentation d'un état des lieux complet du secteur de la restauration à Marrakech, incluant les défis et opportunités.

      2. **Réglementation et facilitation administrative** - Discussion sur les procédures administratives et les moyens de les simplifier pour favoriser le développement du secteur.

      3. **Tourisme gastronomique** - Stratégies pour positionner Marrakech comme destination gastronomique de premier plan au niveau international.

      4. **Formation et emploi** - Initiatives pour améliorer la formation professionnelle et répondre aux besoins en main-d'œuvre qualifiée.

      5. **Partenariats publics-privés** - Exploration de nouvelles formes de collaboration entre le secteur public et les restaurateurs.

      Cette rencontre s'inscrit dans le cadre de notre mission de représentation et de défense des intérêts de nos membres auprès des autorités locales.
    `,
    relatedEvents: [{
      id: 'reunion-tourisme-2025',
      title: 'Réunion sur le Tourisme Gastronomique',
      date: '22 Janvier 2025'
    }, {
      id: 'formation-hygiene-2025',
      title: 'Formation Hygiène et Sécurité',
      date: '5 Février 2025'
    }]
  };
  useSEO(`${event.title} - ARWM`, event.description);
  return <div className="w-full" data-id="element-370">
      {/* Hero Image */}
      <div className="h-[400px] bg-cover bg-center relative" style={{
      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${event.image})`
    }} data-id="element-371">
        <div className="max-w-[1280px] mx-auto px-4 h-full flex items-end pb-12" data-id="element-372">
          <Link to="/events" className="absolute top-8 left-8 flex items-center text-white font-josefin hover:underline" data-id="element-373">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-374" />
            Retour aux événements
          </Link>
        </div>
      </div>

      {/* Content */}
      <section className="py-20 px-4 bg-white" data-id="element-375">
        <div className="max-w-[1280px] mx-auto" data-id="element-376">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-377">
            {/* Main Content */}
            <div className="lg:col-span-2" data-id="element-378">
              <h1 className="font-cormorant text-5xl font-bold text-arwm-dark-text mb-6" data-id="element-379">
                {event.title}
              </h1>

              <div className="flex flex-wrap gap-6 mb-8 font-josefin text-arwm-dark-text" data-id="element-380">
                <div className="flex items-center" data-id="element-381">
                  <Calendar className="w-5 h-5 mr-2 text-arwm-maroon" data-id="element-382" />
                  <span data-id="element-383">{event.date}</span>
                </div>
                <div className="flex items-center" data-id="element-384">
                  <Clock className="w-5 h-5 mr-2 text-arwm-maroon" data-id="element-385" />
                  <span data-id="element-386">{event.time}</span>
                </div>
                <div className="flex items-center" data-id="element-387">
                  <MapPin className="w-5 h-5 mr-2 text-arwm-maroon" data-id="element-388" />
                  <span data-id="element-389">{event.location}</span>
                </div>
              </div>

              <div className="prose prose-lg max-w-none font-josefin text-arwm-dark-text" data-id="element-390">
                <p className="text-xl mb-6" data-id="element-391">{event.description}</p>
                {event.content.split('\n\n').map((paragraph, index) => {
                if (paragraph.startsWith('**') && paragraph.endsWith(':**')) {
                  return <h2 key={index} className="font-cormorant text-3xl font-bold text-arwm-maroon mt-8 mb-4" data-id="element-392">
                        {paragraph.replace(/\*\*/g, '').replace(':', '')}
                      </h2>;
                }
                if (paragraph.match(/^\d+\./)) {
                  return <p key={index} className="mb-4" data-id="element-393">
                        {paragraph}
                      </p>;
                }
                return <p key={index} className="mb-6" data-id="element-394">
                      {paragraph}
                    </p>;
              })}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1" data-id="element-395">
              <Card data-id="element-396">
                <CardHeader data-id="element-397">
                  <CardTitle className="font-cormorant text-2xl" data-id="element-398">
                    Événements à venir
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-399">
                  <div className="space-y-6" data-id="element-400">
                    {event.relatedEvents.map(relatedEvent => <div key={relatedEvent.id} data-id="element-401">
                        <Link to={`/event-post/${relatedEvent.id}`} className="group" data-id="element-402">
                          <p className="font-josefin text-sm text-arwm-maroon mb-2" data-id="element-403">
                            {relatedEvent.date}
                          </p>
                          <h3 className="font-cormorant text-lg font-bold text-arwm-dark-text group-hover:text-arwm-maroon transition-colors" data-id="element-404">
                            {relatedEvent.title}
                          </h3>
                        </Link>
                      </div>)}
                  </div>
                  <Link to="/events" className="inline-block mt-6 font-josefin text-arwm-maroon text-sm font-semibold hover:underline" data-id="element-405">
                    Voir tous les événements →
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>;
}